'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';

interface Member {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Member' | 'Viewer';
  initials: string;
  color: string;
  joinedDate: string;
}

interface InviteFormData {
  email: string;
  role: 'Admin' | 'Member' | 'Viewer';
}

const INITIAL_MEMBERS: Member[] = [
  { id: 'pm-001', name: 'Alex Morgan', email: 'alex@taskflow.app', role: 'Admin', initials: 'AM', color: '#6366F1', joinedDate: 'Jan 12, 2026' },
  { id: 'pm-002', name: 'Priya Sharma', email: 'priya@taskflow.app', role: 'Member', initials: 'PS', color: '#34D399', joinedDate: 'Jan 15, 2026' },
  { id: 'pm-003', name: 'Jordan Lee', email: 'jordan@taskflow.app', role: 'Member', initials: 'JL', color: '#FBBF24', joinedDate: 'Feb 3, 2026' },
  { id: 'pm-004', name: 'Casey Rivera', email: 'casey@taskflow.app', role: 'Viewer', initials: 'CR', color: '#F87171', joinedDate: 'Feb 20, 2026' },
  { id: 'pm-005', name: 'Sam Okafor', email: 'sam@taskflow.app', role: 'Member', initials: 'SO', color: '#818CF8', joinedDate: 'Mar 5, 2026' },
];

const roleVariantMap: Record<string, 'active' | 'on-hold' | 'archived' | 'info'> = {
  Admin: 'active',
  Member: 'info',
  Viewer: 'archived',
};

export default function ProjectTab() {
  const [members, setMembers] = useState<Member[]>(INITIAL_MEMBERS);
  const [projectName, setProjectName] = useState('Mobile App Redesign');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(projectName);
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<Member | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [changingRole, setChangingRole] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetInvite,
  } = useForm<InviteFormData>({ defaultValues: { role: 'Member' } });

  const handleSaveName = () => {
    if (nameInput.trim().length < 3) {
      toast.error('Project name must be at least 3 characters');
      return;
    }
    setProjectName(nameInput.trim());
    setEditingName(false);
    // BACKEND INTEGRATION: PATCH /api/projects/:projectId with { name }
    toast.success('Project name updated');
  };

  const handleInvite = async (data: InviteFormData) => {
    setInviteLoading(true);
    // BACKEND INTEGRATION: POST /api/projects/:projectId/invites with { email, role }
    await new Promise((r) => setTimeout(r, 900));
    const newMember: Member = {
      id: `pm-${Date.now()}`,
      name: data.email.split('@')[0],
      email: data.email,
      role: data.role,
      initials: data.email.slice(0, 2).toUpperCase(),
      color: '#6B6B80',
      joinedDate: 'just now',
    };
    setMembers((prev) => [...prev, newMember]);
    resetInvite();
    setInviteLoading(false);
    toast.success(`Invite sent to ${data.email}`);
  };

  const handleRemoveMember = (member: Member) => {
    // BACKEND INTEGRATION: DELETE /api/projects/:projectId/members/:memberId
    setMembers((prev) => prev.filter((m) => m.id !== member.id));
    setRemoveMemberTarget(null);
    toast.success(`${member.name} removed from project`);
  };

  const handleRoleChange = (memberId: string, newRole: 'Admin' | 'Member' | 'Viewer') => {
    // BACKEND INTEGRATION: PATCH /api/projects/:projectId/members/:memberId with { role }
    setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, role: newRole } : m));
    setChangingRole(null);
    toast.success('Role updated');
  };

  return (
    <div className="space-y-5">
      {/* Project name */}
      <div className="card-base p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4">Project Details</h2>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="proj-name-edit">
            Project name
          </label>
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                id="proj-name-edit"
                type="text"
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') { setEditingName(false); setNameInput(projectName); }
                }}
                className="input-field flex-1"
              />
              <button onClick={handleSaveName} className="btn-primary h-9 px-3">
                <Icon name="CheckIcon" size={15} />
              </button>
              <button
                onClick={() => { setEditingName(false); setNameInput(projectName); }}
                className="btn-ghost h-9 px-3"
              >
                <Icon name="XMarkIcon" size={15} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 rounded-lg bg-input border border-border">
              <span className="text-sm font-medium text-foreground">{projectName}</span>
              <button
                onClick={() => setEditingName(true)}
                className="p-1 rounded hover:bg-secondary transition-colors"
                aria-label="Edit project name"
              >
                <Icon name="PencilIcon" size={14} className="text-muted-foreground" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Members list */}
      <div className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground">
            Team Members
            <span className="ml-2 text-xs font-normal text-muted-foreground">({members.length})</span>
          </h2>
        </div>

        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={`member-row-${member.id}`}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors group"
            >
              {/* Avatar */}
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ backgroundColor: member.color, color: '#fff' }}
              >
                {member.initials}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground truncate">{member.name}</p>
                  {member.id === 'pm-001' && (
                    <span className="text-[10px] text-muted-foreground">(you)</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{member.email}</p>
              </div>

              {/* Role — click to change */}
              <div className="relative flex-shrink-0">
                {changingRole === member.id ? (
                  <select
                    autoFocus
                    defaultValue={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value as any)}
                    onBlur={() => setChangingRole(null)}
                    className="input-field text-xs h-7 py-0 pr-6 w-28"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Member">Member</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                ) : (
                  <button
                    onClick={() => member.id !== 'pm-001' && setChangingRole(member.id)}
                    disabled={member.id === 'pm-001'}
                    className={`${member.id === 'pm-001' ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}`}
                    title={member.id === 'pm-001' ? 'Cannot change your own role' : 'Click to change role'}
                  >
                    <Badge variant={roleVariantMap[member.role]}>
                      {member.role}
                    </Badge>
                  </button>
                )}
              </div>

              {/* Remove button */}
              {member.id !== 'pm-001' && (
                <button
                  onClick={() => setRemoveMemberTarget(member)}
                  className="p-1.5 rounded-lg hover:bg-danger/10 transition-colors opacity-0 group-hover:opacity-100"
                  aria-label={`Remove ${member.name}`}
                  title={`Remove ${member.name} from project`}
                >
                  <Icon name="UserMinusIcon" size={15} className="text-danger" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Invite member */}
      <div className="card-base p-5">
        <h2 className="text-sm font-semibold text-foreground mb-1">Invite Team Member</h2>
        <p className="text-xs text-muted-foreground mb-4">
          They'll receive an email with a link to join this project.
        </p>

        <form onSubmit={handleSubmit(handleInvite)} noValidate className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="invite-email">
                Email address
              </label>
              <input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                className="input-field"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
            </div>
            <div className="w-36">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="invite-role">
                Role
              </label>
              <select id="invite-role" className="input-field" {...register('role')}>
                <option value="Admin">Admin</option>
                <option value="Member">Member</option>
                <option value="Viewer">Viewer</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-secondary/50">
            <Icon name="InformationCircleIcon" size={14} className="text-primary flex-shrink-0" />
            <span>
              <span className="text-foreground font-medium">Admin</span> — full access ·{' '}
              <span className="text-foreground font-medium">Member</span> — create &amp; edit ·{' '}
              <span className="text-foreground font-medium">Viewer</span> — read only
            </span>
          </div>

          <button type="submit" disabled={inviteLoading} className="btn-primary h-9">
            {inviteLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                Sending invite…
              </>
            ) : (
              <>
                <Icon name="EnvelopeIcon" size={14} />
                Send Invite
              </>
            )}
          </button>
        </form>
      </div>

      {/* Danger zone */}
      <div className="card-base p-5 border border-danger/20">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="ExclamationTriangleIcon" size={16} className="text-danger" />
          <h2 className="text-sm font-semibold text-danger">Danger Zone</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-danger/5 border border-danger/10">
            <div>
              <p className="text-sm font-medium text-foreground">Archive project</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Archive this project. It will be hidden from the dashboard but all data is preserved.
              </p>
            </div>
            <button className="btn-ghost h-8 px-3 text-xs flex-shrink-0">
              Archive
            </button>
          </div>

          <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-danger/5 border border-danger/10">
            <div>
              <p className="text-sm font-medium text-foreground">Delete project</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Permanently delete this project, all {47} cards, and remove all {members.length} members. Cannot be undone.
              </p>
            </div>
            <button
              onClick={() => setDeleteProjectOpen(true)}
              className="btn-danger h-8 px-3 text-xs flex-shrink-0"
            >
              <Icon name="TrashIcon" size={13} />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Remove member confirm */}
      <Modal
        open={!!removeMemberTarget}
        onClose={() => setRemoveMemberTarget(null)}
        title="Remove Member"
        size="sm"
        footer={
          <>
            <button onClick={() => setRemoveMemberTarget(null)} className="btn-ghost">Cancel</button>
            <button
              onClick={() => removeMemberTarget && handleRemoveMember(removeMemberTarget)}
              className="btn-danger"
            >
              <Icon name="UserMinusIcon" size={14} />
              Remove Member
            </button>
          </>
        }
      >
        {removeMemberTarget && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ backgroundColor: removeMemberTarget.color, color: '#fff' }}
              >
                {removeMemberTarget.initials}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{removeMemberTarget.name}</p>
                <p className="text-xs text-muted-foreground">{removeMemberTarget.email}</p>
              </div>
            </div>
            <p className="text-sm text-foreground">
              Remove <span className="font-semibold">{removeMemberTarget.name}</span> from this project?
            </p>
            <p className="text-xs text-muted-foreground">
              They will lose access immediately and their assigned cards will become unassigned.
            </p>
          </div>
        )}
      </Modal>

      {/* Delete project confirm */}
      <Modal
        open={deleteProjectOpen}
        onClose={() => setDeleteProjectOpen(false)}
        title="Delete Project"
        size="sm"
        footer={
          <>
            <button onClick={() => setDeleteProjectOpen(false)} className="btn-ghost">Cancel</button>
            <button
              onClick={() => {
                setDeleteProjectOpen(false);
                toast.success('Project deleted');
              }}
              className="btn-danger"
            >
              <Icon name="TrashIcon" size={14} />
              Permanently Delete
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="w-10 h-10 rounded-lg bg-danger/10 flex items-center justify-center">
            <Icon name="TrashIcon" size={20} className="text-danger" />
          </div>
          <p className="text-sm text-foreground">
            Delete <span className="font-semibold">"{projectName}"</span>?
          </p>
          <p className="text-xs text-muted-foreground">
            This will permanently delete the project, all 47 cards, all activity history, and remove all {members.length} members.
            This action cannot be undone.
          </p>
          <div className="p-3 rounded-lg bg-danger/10 border border-danger/20">
            <p className="text-xs text-danger font-medium">
              Type the project name to confirm deletion is not required here — clicking the button below is irreversible.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}